import React, { useState } from 'react';
import { Plus, Minus, Search, MessageCircle, Phone, Mail, ArrowRight, Instagram, Linkedin, Facebook, Twitter } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeTab, setActiveTab] = useState("General");

  const tabs = ["General", "Design Process", "Budget & Pricing", "Support"];

  // --- EXPANDED FAQ DATA (5 Questions per Category) ---
  const faqs = [
    // 1. GENERAL
    {
      category: "General",
      question: "Where is Luxuriya Homes located?",
      answer: "Our flagship design studio is located in New Delhi, India. However, we execute luxury projects across major cities including Mumbai, Dubai, London, and Singapore."
    },
    {
      category: "General",
      question: "Do you undertake commercial projects?",
      answer: "Yes, we specialize in high-end commercial spaces including luxury hotels, boutique offices, fine-dining restaurants, and premium retail showrooms."
    },
    {
      category: "General",
      question: "What design styles do you specialize in?",
      answer: "We are known for 'Modern Heritage'—a blend of contemporary luxury with timeless classic elements. However, our team is versatile in Art Deco, Minimalism, and Neoclassical styles."
    },
    {
      category: "General",
      question: "Do you offer eco-friendly or sustainable designs?",
      answer: "Absolutely. We prioritize sustainable luxury by sourcing eco-friendly materials, energy-efficient lighting, and non-toxic paints without compromising on aesthetics."
    },
    {
      category: "General",
      question: "Can you work with my existing architect?",
      answer: "Yes, we often collaborate with architects to handle the interior styling, material selection, and furnishing phase of the project."
    },

    // 2. DESIGN PROCESS
    {
      category: "Design Process",
      question: "How does the consultation process work?",
      answer: "It starts with a discovery call to understand your vision. We then schedule a site visit or virtual walkthrough, followed by a mood board presentation and initial layout planning."
    },
    {
      category: "Design Process",
      question: "Do you provide 3D renderings before starting?",
      answer: "Absolutely. We create ultra-realistic 4K 3D renders so you can visualize every texture, light fitting, and furniture piece before a single brick is laid."
    },
    {
      category: "Design Process",
      question: "What is the typical timeline for a project?",
      answer: "A complete luxury home transformation typically takes between 4 to 8 months, depending on the size of the property and the level of customization required."
    },
    {
      category: "Design Process",
      question: "Do I need to be involved in every step?",
      answer: "We offer a 'White Glove' turnkey service, meaning we handle everything from procurement to installation. You can be as involved or as hands-off as you wish."
    },
    {
      category: "Design Process",
      question: "Do you handle procurement of furniture?",
      answer: "Yes, we source bespoke furniture, art, and artifacts from global luxury brands in Italy, France, and locally from master craftsmen in India."
    },

    // 3. BUDGET & PRICING
    {
      category: "Budget & Pricing",
      question: "What is the typical budget for a project?",
      answer: "Since we focus on luxury and bespoke designs, our residential projects typically start from ₹50 Lakhs onwards. However, every project is unique, and we tailor solutions to your specific requirements."
    },
    {
      category: "Budget & Pricing",
      question: "Are there any hidden costs?",
      answer: "Transparency is our core value. We provide a detailed BOQ (Bill of Quantities) at the start. Any changes or additions are approved by you in writing before execution."
    },
    {
      category: "Budget & Pricing",
      question: "What is your payment schedule?",
      answer: "Payments are milestone-based: 10% advance, 40% on design finalization, 40% during execution, and the final 10% upon handover."
    },
    {
      category: "Budget & Pricing",
      question: "Do you charge a separate design fee?",
      answer: "Yes, we have a design fee for the concept and planning phase. If you choose us for the complete execution, a part of this fee is often adjusted into the final project cost."
    },
    {
      category: "Budget & Pricing",
      question: "Can I get a rough estimate before booking?",
      answer: "We can provide a ballpark range based on your floor plan and requirements during our initial discovery call, but accurate pricing requires a site visit."
    },

    // 4. SUPPORT
    {
      category: "Support",
      question: "Do you offer post-completion support?",
      answer: "Yes, all our projects come with a 1-year complimentary service warranty for workmanship. We also offer annual maintenance packages for our premium clients."
    },
    {
      category: "Support",
      question: "What happens if something breaks after handover?",
      answer: "Our dedicated 'Care Team' is just a call away. We will coordinate repairs or replacements for any manufacturing defects or installation issues."
    },
    {
      category: "Support",
      question: "Can you help with styling and art selection?",
      answer: "Yes, we offer a 'Final Layer' service where we curate art, rugs, sculptures, and soft furnishings to complete the look of your home."
    },
    {
      category: "Support",
      question: "Do you provide maintenance guidelines?",
      answer: "Upon handover, you receive a 'Home Care Manual' detailing how to clean and maintain your specific marble, fabrics, and wood finishes."
    },
    {
      category: "Support",
      question: "I want to renovate just one room. Is that possible?",
      answer: "While we specialize in full-home projects, we do take up single-room luxury makeovers (like Master Bedrooms or Home Theaters) on a case-by-case basis."
    }
  ];

  const filteredFaqs = faqs.filter(faq => faq.category === activeTab);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-zinc-50 min-h-screen text-zinc-900 font-sans selection:bg-amber-200 selection:text-black">
      
      {/* ================= 1. HERO SECTION ================= */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-zinc-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000" 
            alt="FAQ Background" 
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 pt-16 max-w-3xl mx-auto">
          <p className="text-amber-400 tracking-[0.3em] uppercase text-xs font-bold mb-4">Support Center</p>
          <h1 className="text-5xl md:text-6xl font-serif text-white mb-8 drop-shadow-xl">How can we help?</h1>
          
          {/* Search Bar */}
          <div className="relative w-full max-w-xl mx-auto">
             <input 
               type="text" 
               placeholder="Search for answers..." 
               className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-full py-4 pl-6 pr-14 text-white placeholder-zinc-400 focus:outline-none focus:border-amber-500 focus:bg-white/20 transition-all shadow-lg"
             />
             <button className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-amber-500 rounded-full hover:bg-white hover:text-black transition-colors">
                <Search size={20} />
             </button>
          </div>
        </div>
      </section>

      {/* ================= 2. MAIN FAQ SECTION ================= */}
      <section className="max-w-5xl mx-auto px-4 py-20 min-h-[600px]">
        
        <div className="flex flex-col md:flex-row gap-12">
           
           {/* LEFT: Categories Sidebar */}
           <div className="md:w-1/4">
              <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-6">Categories</h3>
              <div className="flex flex-col gap-2">
                 {tabs.map(tab => (
                   <button
                     key={tab}
                     onClick={() => { setActiveTab(tab); setOpenIndex(null); }}
                     className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 flex justify-between items-center ${
                       activeTab === tab 
                       ? 'bg-black text-white shadow-lg' 
                       : 'text-zinc-600 hover:bg-zinc-200'
                     }`}
                   >
                     {tab}
                     {activeTab === tab && <div className="w-2 h-2 bg-amber-500 rounded-full"></div>}
                   </button>
                 ))}
              </div>
           </div>

           {/* RIGHT: Accordion Questions */}
           <div className="md:w-3/4">
              <h2 className="text-3xl font-serif text-zinc-900 mb-8">{activeTab} Questions</h2>
              
              <div className="space-y-4">
                 {filteredFaqs.map((faq, index) => (
                   <div 
                     key={index} 
                     className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                       openIndex === index 
                       ? 'border-amber-500 bg-white shadow-md' 
                       : 'border-zinc-200 bg-white hover:border-zinc-300'
                     }`}
                   >
                      <button 
                        onClick={() => toggleAccordion(index)}
                        className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                      >
                        <span className={`font-serif text-lg ${openIndex === index ? 'text-amber-600' : 'text-zinc-800'}`}>
                          {faq.question}
                        </span>
                        {openIndex === index 
                          ? <Minus size={20} className="text-amber-600 flex-shrink-0" /> 
                          : <Plus size={20} className="text-zinc-400 flex-shrink-0" />
                        }
                      </button>

                      <div 
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="p-6 pt-0 text-zinc-500 leading-relaxed border-t border-zinc-50">
                           {faq.answer}
                        </div>
                      </div>
                   </div>
                 ))}
              </div>

              {filteredFaqs.length === 0 && (
                <p className="text-zinc-400 italic">No questions found in this category.</p>
              )}
           </div>

        </div>
      </section>


      {/* ================= 3. STILL HAVE QUESTIONS? (CTA) ================= */}
      <section className="bg-zinc-100 py-20 px-4 border-t border-zinc-200">
         <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-serif text-zinc-900 mb-6">Still have questions?</h2>
            <p className="text-zinc-500 mb-10 max-w-lg mx-auto">
              Can't find the answer you're looking for? Please chat to our friendly team.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
               
               {/* Contact Card 1 */}
               <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-zinc-100 group cursor-pointer">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                     <MessageCircle size={24} />
                  </div>
                  <h4 className="font-bold text-zinc-900 mb-2">Chat to support</h4>
                  <p className="text-zinc-500 text-sm mb-4">We're here to help.</p>
                  <span className="text-amber-600 font-bold text-sm">Start Live Chat</span>
               </div>

               {/* Contact Card 2 */}
               <Link to="/contact" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-zinc-100 group cursor-pointer">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                     <Mail size={24} />
                  </div>
                  <h4 className="font-bold text-zinc-900 mb-2">Email us</h4>
                  <p className="text-zinc-500 text-sm mb-4">Response within 2 hours.</p>
                  <span className="text-amber-600 font-bold text-sm">design@luxuriyahomes.com</span>
               </Link>

            </div>

            {/* --- SOCIAL MEDIA ICONS (Connect With Us) --- */}
            <div className="border-t border-zinc-200 pt-10">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-6">Connect With Us</p>
              <div className="flex justify-center gap-6">
                 <a href="#" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-zinc-600 hover:text-amber-600 hover:shadow-md transition-all">
                    <Instagram size={20} />
                 </a>
                 <a href="#" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-zinc-600 hover:text-amber-600 hover:shadow-md transition-all">
                    <Facebook size={20} />
                 </a>
                 <a href="#" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-zinc-600 hover:text-amber-600 hover:shadow-md transition-all">
                    <Linkedin size={20} />
                 </a>
                 <a href="#" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-zinc-600 hover:text-amber-600 hover:shadow-md transition-all">
                    <Twitter size={20} />
                 </a>
              </div>
            </div>

         </div>
      </section>

      {/* ================= 4. FINAL BANNER ================= */}
      <section className="bg-black text-white py-16 px-4 flex flex-col md:flex-row items-center justify-between max-w-7xl mx-auto rounded-none md:rounded-2xl mb-12 shadow-2xl relative overflow-hidden">
         {/* Decoration */}
         <div className="absolute top-0 right-0 w-64 h-64 bg-amber-600/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
         
         <div className="relative z-10 mb-8 md:mb-0 md:pl-8 text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-serif mb-2">Ready to start your project?</h3>
            <p className="text-zinc-400 text-sm">Join 150+ happy families today.</p>
         </div>
         
         <div className="relative z-10 md:pr-8">
            <Link to="/contact" className="bg-white text-black px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-amber-500 hover:text-white transition-colors rounded-sm flex items-center gap-2">
               Get a Quote <ArrowRight size={16}/>
            </Link>
         </div>
      </section>

    </div>
  );
};

export default FAQ;